import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { format } from 'date-fns';
import he from 'date-fns/locale/he';
import * as MessagesSelectors from '../../modules/messages/selectors';
import { ActionTypes } from '../actions';
import { useNavigate } from 'react-router-dom';

const Messages = ({ messages, loading, onMount }) => {
  const navigate = useNavigate();

  useEffect(() => {
    onMount({ page: 0, take: 50 });
  }, [onMount]);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('[Polling] Checking for new messages in archive...');
      onMount({ page: 0, take: 50 });
    }, 30000); // 30 seconds

    return () => {
      clearInterval(interval);
    };
  }, [onMount]);

  const handleMessageClick = (message) => {
    if (message.inquiry?.id) {
      navigate(`/applications/pending/${message.inquiry.id}`);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: he });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          התכתבויות
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {messages.length} הודעות
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }}>
        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
            <Typography variant="h6">אין הודעות להצגה</Typography>
            <Typography variant="body2">
              הודעות מלקוחות יופיעו כאן
            </Typography>
          </Box>
        ) : (
          <List>
            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    mb: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: !message.isRead ? '#e3f2fd' : 'transparent',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                  disablePadding
                >
                  <ListItemButton onClick={() => handleMessageClick(message)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <span style={{ fontWeight: 500 }}>
                            {message.direction === 'incoming' ? 'מ: ' : 'אל: '}
                            {message.direction === 'incoming' 
                              ? message.fromEmail 
                              : message.toEmail}
                          </span>
                          <span style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                            {formatDate(message.createdAt)}
                          </span>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography sx={{ mb: 0.5 }} component="div">
                            {message.subject}
                          </Typography>
                          <Typography 
                            sx={{ 
                              fontSize: '0.875rem', 
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }} 
                            component="div"
                          >
                            {message.body}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {message.direction === 'incoming' && (
                              <Chip label="נכנס" size="small" color="primary" />
                            )}
                            {message.direction === 'outgoing' && (
                              <Chip label="יוצא" size="small" color="secondary" />
                            )}
                            {message.inquiry && (
                              <Chip 
                                label={`פניה #${message.inquiry.id}`} 
                                size="small" 
                                variant="outlined" 
                              />
                            )}
                            {message.files && message.files.length > 0 && (
                              <Chip 
                                label={`${message.files.length} קבצים`} 
                                size="small" 
                                variant="outlined" 
                              />
                            )}
                          </Box>
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  messages: MessagesSelectors.messages(state),
  loading: MessagesSelectors.isLoading(state),
});

const mapDispatchToProps = (dispatch) => ({
  onMount: (params) => dispatch({ type: ActionTypes.MESSAGES_PAGE_MOUNTED, payload: params }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Messages);

